import type {FC, UIEvent} from 'react';
import {useState, useMemo} from 'react';
import {useQuery, useInfiniteQuery} from '@tanstack/react-query';
import type {AccountsCheckinsListParams} from 'api/v2';
import {Box, Paper, Stack, Typography} from '@wellstone-solutions/web';
import {
  membersQueryOptions,
  checkinsQueryOptions,
  journalQueryOptions,
} from 'queries';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {RowCount} from 'modules/datagrid';
import {CheckinsCard} from './CheckinsCard';
import {FilterForm} from './FilterForm';
import {Theme} from '@wellstone-solutions/common';

interface CheckinsListProps {
  memberId: string;
}

export const CheckinsList: FC<CheckinsListProps> = ({memberId}) => {
  const [listParams, setListParams] = useState<AccountsCheckinsListParams>({});
  const membersQuery = useQuery(membersQueryOptions.detail(memberId));

  // TODO - Remove this after api-client type is fixed
  // eslint-disable-next-line
  // @ts-ignore
  const userId = membersQuery?.data?.user.id;

  const checkinsQuery = useInfiniteQuery({
    ...checkinsQueryOptions.infiniteList({userId, ...listParams}),
    enabled: !!userId,
  });

  // Fetch all user journals upfront to avoid N+1 queries
  const journalsQuery = useQuery({
    ...journalQueryOptions.userJournals({
      userId: userId || '',
      pageSize: 100,
    }),
    enabled: !!userId,
  });

  const isLoading =
    checkinsQuery.isLoading ||
    membersQuery.isLoading ||
    journalsQuery.isLoading;

  // Create a mapping of journalId to journal data for efficient lookup
  const journalMap = useMemo(() => {
    if (!journalsQuery.data?.results) return new Map();

    const map = new Map();
    journalsQuery.data.results.forEach((journal) => {
      map.set(journal.id, journal);
    });
    return map;
  }, [journalsQuery.data?.results]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.5 &&
      !checkinsQuery.isFetchingNextPage &&
      checkinsQuery.hasNextPage
    ) {
      checkinsQuery.fetchNextPage();
    }
  };

  const hasNoResults =
    !isLoading && checkinsQuery.data?.pages[0]?.data.count === 0;

  return (
    <Box sx={{flex: 1}}>
      <Stack direction="row" justifyContent="space-between" sx={{mb: 1}}>
        <Typography variant="h6">Check In's</Typography>
        <FilterForm onSubmit={setListParams} />
      </Stack>

      <Typography variant="body2" sx={{mb: 2}}>
        A chronological record of a member's check in's that tracks their
        progress over time.
      </Typography>

      <RowCount
        isLoading={isLoading}
        label="result"
        count={checkinsQuery.data?.pages[0]?.data.count || 0}
      />

      <Paper
        sx={styles.container}
        onScroll={handleScroll}
        data-testid="checkins-list">
        {isLoading && <LoadingIndicator />}

        {hasNoResults ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
            <Typography
              variant="h6"
              color={Theme.colorPalette.onSurface}
              fontWeight="bold">
              Sorry, no results found.
            </Typography>
          </Box>
        ) : (
          <>
            {checkinsQuery.data?.pages.map((page, pageNumber) => (
              <Box key={pageNumber}>
                {page.data.results.map((checkin) => (
                  <CheckinsCard
                    key={checkin.id}
                    checkin={checkin}
                    journal={
                      checkin.journalId
                        ? journalMap.get(checkin.journalId)
                        : null
                    }
                  />
                ))}
              </Box>
            ))}

            {checkinsQuery.isFetchingNextPage && (
              <LoadingIndicator size={24} stylesOverride={styles.loadingMore} />
            )}

            {!checkinsQuery.hasNextPage && !isLoading && (
              <Typography variant="caption" sx={{textAlign: 'center'}}>
                You've reached the end of the list
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

const styles = {
  container: {
    height: '500px',
    overflowY: 'auto',
    padding: 2,
  },
  loadingMore: {
    padding: 2,
    marginBottom: 0,
    height: 'auto',
  },
};
